import { Briefcase, Lock, Package, Receipt, Ticket } from "lucide-react";

import { CURRENT_BRAND } from "@/brand/config";
import { money } from "@/lib/validators";
import { BrandMark } from "./BrandMark";
import type { CartItem } from "@/lib/api";

const ICONS: Record<string, typeof Briefcase> = {
  briefcase: Briefcase,
  receipt: Receipt,
};

type Totals = { subtotal: string; tax: string; total: string };

type RedeemPackage = {
  name: string;
  product: string;
  vence?: string;
};

export function SummaryRail({
  flow,
  empty,
  items,
  totals,
  pkg,
}: {
  flow: "cart" | "code";
  empty: boolean;
  items: CartItem[];
  totals: Totals;
  pkg?: RedeemPackage | null;
}) {
  return (
    <aside className="rail" aria-label="Resumen del proceso">
      <div className="rail__brand">
        <BrandMark />
        <span className="brand__name">{CURRENT_BRAND.displayName}</span>
      </div>

      <div className="rail__body">
        {flow === "code" && pkg ? <RailPackage pkg={pkg} /> : null}
        {flow === "cart" && !empty && items.length > 0 ? (
          <RailCart items={items} totals={totals} />
        ) : null}
        {flow === "cart" && (empty || items.length === 0) ? <RailEmpty /> : null}
      </div>

      <div className="rail__foot">
        <Lock size={14} strokeWidth={1.6} />
        <span>Procesado de forma segura · TLS 1.3</span>
      </div>
    </aside>
  );
}

function RailCart({ items, totals }: { items: CartItem[]; totals: Totals }) {
  return (
    <>
      <div className="eyebrow">Tu pedido</div>
      <div className="rail__items">
        {items.map((it) => {
          const Icon =
            ICONS[(it as CartItem & { icon?: string }).icon || ""] || Briefcase;
          return (
            <div className="rail__item" key={it.id || it.sku}>
              <span className="rail__item-ic">
                <Icon size={18} strokeWidth={1.5} />
              </span>
              <span className="rail__item-body">
                <span className="rail__item-title">{it.title || it.sku}</span>
                {it.meta && (
                  <span className="rail__item-meta">{it.meta}</span>
                )}
              </span>
              <span className="rail__item-price tnum">
                {money(it.lineTotal)}
              </span>
            </div>
          );
        })}
      </div>

      <div className="rail__sep"></div>

      <div className="rail__items" style={{ gap: 8 }}>
        <div className="rail__item">
          <span className="rail__item-body">
            <span
              className="rail__item-title"
              style={{ fontWeight: 400, color: "hsl(var(--text-secondary))" }}
            >
              Subtotal
            </span>
          </span>
          <span className="rail__item-price tnum">{money(totals.subtotal)}</span>
        </div>
        <div className="rail__item">
          <span className="rail__item-body">
            <span
              className="rail__item-title"
              style={{ fontWeight: 400, color: "hsl(var(--text-secondary))" }}
            >
              IVA (16%)
            </span>
          </span>
          <span className="rail__item-price tnum">{money(totals.tax)}</span>
        </div>
        <div className="rail__item" style={{ marginTop: 4 }}>
          <span className="rail__item-body">
            <span className="rail__item-title">Total</span>
          </span>
          <span className="rail__item-price tnum">{money(totals.total)}</span>
        </div>
      </div>
    </>
  );
}

function RailPackage({ pkg }: { pkg: RedeemPackage }) {
  return (
    <>
      <div className="eyebrow">Vas a activar</div>
      <div className="rail__pkg">
        <span className="rail__pkg-ic">
          <Ticket size={20} strokeWidth={1.5} />
        </span>
        <span className="rail__pkg-text">
          <span className="rail__item-title">{pkg.name}</span>
          <span className="rail__item-meta">{pkg.product}</span>
        </span>
      </div>

      {pkg.vence && (
        <>
          <div className="rail__sep"></div>
          <div className="rail__item">
            <span className="rail__item-body">
              <span
                className="rail__item-title"
                style={{ fontWeight: 400, color: "hsl(var(--text-secondary))" }}
              >
                Vence
              </span>
            </span>
            <span className="rail__item-price tnum">{pkg.vence}</span>
          </div>
        </>
      )}
    </>
  );
}

function RailEmpty() {
  return (
    <div className="rail__empty">
      <Package size={36} strokeWidth={1.3} />
      <div>Tu carrito está vacío.</div>
      <div style={{ fontSize: 12.5 }}>
        Ingresa un código de invitación para activar tu cuenta.
      </div>
    </div>
  );
}
